import createClient, { type Middleware } from "openapi-fetch";
import type { paths, components } from "./gen/schema";

/**
 * THE STAFF API CLIENT.
 *
 * Generated from `backend/api/openapi.yaml` — never hand-written (§6). `npm run generate`
 * regenerates `gen/schema.d.ts`; every path, body and response type below is derived from
 * it, so an endpoint that changes shape in the contract is a TypeScript error here rather
 * than a runtime surprise in front of a customer.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080";

/**
 * THE STAFF TOKEN IS NOT THE CUSTOMER TOKEN, AND THEY ARE NOT STORED TOGETHER.
 *
 * The API reads the staff session from its own cookie (`tankly_staff_session`) — a
 * different name from the customer app's, so a customer session presented here does not
 * authenticate and vice versa. This key is the bearer fallback for the dev split
 * (localhost:3001 → localhost:8080), where a cross-origin cookie will not be set.
 *
 * `tankly.staff.token` — namespaced deliberately. If this ever collided with the customer
 * app's `tankly.token`, a staff engineer's browser could send a staff credential to a
 * customer surface, or worse.
 */
const STAFF_TOKEN_KEY = "tankly.staff.token";

/**
 * The IMPERSONATION token is stored under its own key, and it is deliberately a SESSION
 * key (sessionStorage, not localStorage): closing the tab ends the impersonation, and it
 * cannot outlive the browser session the way a forgotten localStorage entry would.
 */
const IMPERSONATION_KEY = "tankly.staff.impersonation";

export const staffToken = {
  get(): string | null {
    try {
      return localStorage.getItem(STAFF_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string | null): void {
    try {
      if (token) localStorage.setItem(STAFF_TOKEN_KEY, token);
      else localStorage.removeItem(STAFF_TOKEN_KEY);
    } catch {
      /* private mode — the session simply does not survive a reload */
    }
  },
};

/** An impersonation in progress: the read-only customer token, and who it belongs to. */
export interface Impersonation {
  accessToken: string;
  organizationName: string;
  organizationId: string;
  userName: string;
  expiresAt: string;
  auditId: string;
}

export const impersonation = {
  get(): Impersonation | null {
    try {
      const raw = sessionStorage.getItem(IMPERSONATION_KEY);
      return raw ? (JSON.parse(raw) as Impersonation) : null;
    } catch {
      return null;
    }
  },
  set(value: Impersonation | null): void {
    try {
      if (value) sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(value));
      else sessionStorage.removeItem(IMPERSONATION_KEY);
    } catch {
      /* private mode */
    }
  },
  /** Expired impersonations are not impersonations. The server agrees — the session is dead. */
  isLive(value: Impersonation | null): value is Impersonation {
    return value !== null && new Date(value.expiresAt).getTime() > Date.now();
  },
};

const staffAuth: Middleware = {
  onRequest({ request }) {
    const token = staffToken.get();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
};

/** The STAFF client. Every /v1/admin/* call goes through it, and it never carries a customer token. */
export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  credentials: "include",
  // Resolve fetch at CALL time so the test's fetch stub is not bypassed by a reference
  // captured at module-eval time.
  fetch: (request) => globalThis.fetch(request),
});

api.use(staffAuth);

/**
 * The IMPERSONATION client. A separate instance, carrying the customer's read-only token
 * and NEVER the staff one — so a staff credential cannot leak onto a customer endpoint by
 * a middleware ordering accident.
 *
 * It can only read. The server refuses every write it makes (403), by design: support means
 * "see what they see", never "act as them".
 */
export const customerApi = createClient<paths>({
  baseUrl: API_BASE_URL,
  fetch: (request) => globalThis.fetch(request),
});

customerApi.use({
  onRequest({ request }) {
    const live = impersonation.get();
    if (impersonation.isLive(live)) {
      request.headers.set("Authorization", `Bearer ${live.accessToken}`);
    }
    return request;
  },
});

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** An RFC 7807 problem, as the API actually sends it. */
export class ApiError extends Error {
  readonly status: number;
  readonly title: string;

  constructor(status: number, title: string, detail: string) {
    super(detail || title);
    this.name = "ApiError";
    this.status = status;
    this.title = title;
  }
}

type Problem = components["schemas"]["Problem"];

/** Collapse openapi-fetch's `{data, error}` into "value, or throw". React Query wants a rejection. */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await promise;
  } catch (cause) {
    throw new ApiError(0, "Network error", cause instanceof Error ? cause.message : "The API is unreachable.");
  }

  const { data, error, response } = result;
  if (error !== undefined || !response.ok) {
    const problem = error as Problem | undefined;
    throw new ApiError(
      response.status,
      problem?.title ?? "Request failed",
      problem?.detail ?? `The API returned ${response.status}.`,
    );
  }
  return data as T;
}

// The staff read model, straight off the contract. No hand-written shapes.
export type AdminOrganization = components["schemas"]["AdminOrganization"];
export type PlatformMetrics = components["schemas"]["PlatformMetrics"];
export type ImpersonationSession = components["schemas"]["ImpersonationSession"];
export type OrganizationStatus = components["schemas"]["OrganizationStatus"];
